import React, { Fragment, useCallback, useEffect, useState } from 'react';

import { Box } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import Liff, { getLineId } from '@/client/utils/liff';
import { secureRandomInt } from '@/client/utils/random';
import { PRIZE_STATUS } from './constants';
import { PrizeDialog } from './dialog';
import styles from './styles';

const SERVER_URL = __SERVER_URL__;

const useStyles = makeStyles(createStyles(styles));

const Prize = ({ config }) => {
  const { id, partNum, prizeButton, prizeBackground, prizeSpin, prizeWheel } = config;

  const [duration, setDuration] = useState(0);
  const [degree, setDegree] = useState(0);
  const [timing, setTiming] = useState('linear');
  const [status, setStatus] = useState('');
  const [link, setLink] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [animating, setAnimating] = useState(false);

  const degreeMap = {
    [PRIZE_STATUS.PRIZE_RECEIVED]: [5, 95, 185, 275],
    [PRIZE_STATUS.PRIZE_MISSED]: [50, 140, 230, 320],
  };

  const classes = useStyles({ deg: degree, duration, timing });

  const drawPrize = async () => {
    // deny multiple draws
    if (duration > 0 && status) {
      setStatus(PRIZE_STATUS.PRIZE_DRAWN);
      setDialogOpen(true);
      return;
    }

    setTiming('linear');
    setDuration(0);
    setDegree(0);
    setTimeout(() => {
      setDuration(3);
      setDegree(deg => deg + 2520);
    }, 100);

    try {
      const lineId = await getLineId();
      const resp = await fetch(`${SERVER_URL}/disease-quiz/draw-prize`, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        method: 'POST',
        body: JSON.stringify({ lineId, diseaseId: id, partNum }),
      });
      if (!resp.ok) {
        const { errMsg } = await resp.json();
        throw new Error(errMsg);
      }
      const { status: respStatus, link: respLink } = await resp.json();
      setStatus(respStatus);
      setLink(respLink);

      if ([PRIZE_STATUS.PRIZE_DRAWN, PRIZE_STATUS.PRIZE_FULL].includes(respStatus)) {
        setDialogOpen(true);
        return;
      }

      setTimeout(() => {
        // this callback is only for missed and received
        if (![PRIZE_STATUS.PRIZE_MISSED, PRIZE_STATUS.PRIZE_RECEIVED].includes(respStatus)) {
          return;
        }
        setTiming('ease-out');
        const targetDegree =
          degreeMap[respStatus][secureRandomInt(0, degreeMap[respStatus].length)] + secureRandomInt(0, 35);
        setDuration(3);
        setDegree(deg => deg + targetDegree);
        setTimeout(() => {
          setDialogOpen(true);
        }, 3200);
      }, 2000);
    } catch (error) {
      setStatus('');
      setDialogOpen(true);
      throw new Error(`failed to draw prize: ${error}`);
    }
  };

  const handleClickDialogAction = () => {
    if (status === PRIZE_STATUS.PRIZE_RECEIVED) {
      window.location.href = link;
      return;
    }
    if ([PRIZE_STATUS.PRIZE_FULL, PRIZE_STATUS.PRIZE_MISSED].includes(status)) {
      Liff.closeWindow();
      return;
    }
    // unexpected API error
    setDialogOpen(false);
  };

  const handleTransitionStart = useCallback(() => {
    setAnimating(true);
  }, []);

  const handleTransitionEnd = useCallback(() => {
    setAnimating(false);
  });

  useEffect(() => {
    const prizeImage = document.getElementById('prizeImage');
    prizeImage.addEventListener('transitionstart', handleTransitionStart, false);
    prizeImage.addEventListener('transitionend', handleTransitionEnd, false);

    return () => {
      prizeImage.removeEventListener('transitionstart', handleTransitionStart, false);
      prizeImage.removeEventListener('transitionend', handleTransitionEnd, false);
    };
  }, []);

  return (
    <Fragment>
      <Box>
        <Box className={classes.wheelContainer} style={{ backgroundImage: `url(${prizeBackground})` }}>
          <Box className={classes.wheel} style={{ pointerEvents: animating ? 'none' : 'auto' }}>
            <Box className={classes.button} onClick={drawPrize}>
              <img src={prizeButton} className={classes.buttonImage} alt="button" />
            </Box>
            <img src={prizeWheel} className={classes.wheelImage} alt="wheel" />
            <img id="prizeImage" src={prizeSpin} className={classes.prizeImage} alt="prize" />
          </Box>
        </Box>
      </Box>
      <PrizeDialog open={dialogOpen} status={status} config={config} onClick={handleClickDialogAction} />
    </Fragment>
  );
};

export default Prize;
